/**
 * Low-level Web Audio primitives shared by every synthesized sound in the
 * game (hits, footsteps, ambient beds, UI blips) — see
 * `config/audio-tuning.ts`'s file comment for why this is fully synthesized
 * rather than sample-based. Every `play*` function is fire-and-forget: it
 * builds a small disposable node graph, schedules it, and disconnects
 * itself on completion — callers never hold a reference or manage cleanup.
 */

const noiseBufferCache = new Map<string, AudioBuffer>();

/** A cached buffer of white noise, keyed by (sampleRate, duration) — shared across every noise-based sound rather than regenerated per play. */
export function getNoiseBuffer(context: AudioContext, durationS: number): AudioBuffer {
  const key = `${context.sampleRate}:${durationS}`;
  let buffer = noiseBufferCache.get(key);
  if (!buffer) {
    const frameCount = Math.max(1, Math.floor(context.sampleRate * durationS));
    buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frameCount; i++) data[i] = Math.random() * 2 - 1;
    noiseBufferCache.set(key, buffer);
  }
  return buffer;
}

export interface EnvelopeSpec {
  attackS: number;
  decayS: number;
  peakGain: number;
}

/** Linear attack, exponential decay (exponential ramps can't target exactly 0, hence the 0.0001 floor) — applied to a GainNode's gain param starting at `startTime`. Returns the time the envelope finishes. */
function applyEnvelope(gainParam: AudioParam, startTime: number, spec: EnvelopeSpec): number {
  const { attackS, decayS, peakGain } = spec;
  gainParam.cancelScheduledValues(startTime);
  gainParam.setValueAtTime(0.0001, startTime);
  gainParam.linearRampToValueAtTime(Math.max(0.0001, peakGain), startTime + attackS);
  gainParam.exponentialRampToValueAtTime(0.0001, startTime + attackS + decayS);
  return startTime + attackS + decayS;
}

export interface NoiseBurstSpec {
  durationS: number;
  filterType: BiquadFilterType;
  filterFreq: number;
  filterQ?: number;
  envelope: EnvelopeSpec;
  playbackRate?: number;
  pan?: number;
  delayS?: number;
}

/** One noise burst through a filter + envelope + optional stereo pan, connected to `destination`. */
export function playNoiseBurst(context: AudioContext, destination: AudioNode, spec: NoiseBurstSpec): void {
  const buffer = getNoiseBuffer(context, spec.durationS);
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.playbackRate.value = spec.playbackRate ?? 1;

  const filter = context.createBiquadFilter();
  filter.type = spec.filterType;
  filter.frequency.value = spec.filterFreq;
  if (spec.filterQ !== undefined) filter.Q.value = spec.filterQ;

  const gain = context.createGain();
  const startTime = context.currentTime + (spec.delayS ?? 0);
  const endTime = applyEnvelope(gain.gain, startTime, spec.envelope);

  const panner = context.createStereoPanner();
  panner.pan.value = spec.pan ?? 0;

  source.connect(filter).connect(gain).connect(panner).connect(destination);
  source.start(startTime);
  source.stop(endTime + 0.05);
  source.onended = () => {
    source.disconnect();
    filter.disconnect();
    gain.disconnect();
    panner.disconnect();
  };
}

export interface ToneSpec {
  freq: number;
  type: OscillatorType;
  envelope: EnvelopeSpec;
  detune?: number;
  pan?: number;
  delayS?: number;
  /**
   * Where the pitch lands by the end of the envelope, in Hz. Omitted means a
   * flat note.
   *
   * Added for the ability cast cues (2026-08-26): the direction of a sweep is
   * most of what separates "a ward went up" from "someone vanished", and two
   * stacked flat tones read as a chord rather than as a slide.
   */
  sweepToHz?: number;
}

/** One oscillator tone through an envelope + optional stereo pan, connected to `destination`. */
export function playTone(context: AudioContext, destination: AudioNode, spec: ToneSpec): void {
  const osc = context.createOscillator();
  osc.type = spec.type;
  osc.frequency.value = spec.freq;
  if (spec.detune) osc.detune.value = spec.detune;

  const gain = context.createGain();
  const startTime = context.currentTime + (spec.delayS ?? 0);
  const endTime = applyEnvelope(gain.gain, startTime, spec.envelope);
  // Exponential rather than linear: pitch is perceived logarithmically, so a
  // linear ramp spends most of its time in the last octave. Guarded above
  // zero because `exponentialRampToValueAtTime` throws on a zero target.
  if (spec.sweepToHz && spec.sweepToHz > 0) {
    osc.frequency.setValueAtTime(spec.freq, startTime);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, spec.sweepToHz), endTime);
  }

  const panner = context.createStereoPanner();
  panner.pan.value = spec.pan ?? 0;

  osc.connect(gain).connect(panner).connect(destination);
  osc.start(startTime);
  osc.stop(endTime + 0.05);
  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
    panner.disconnect();
  };
}

/** A long, looping filtered-noise floor — used only by the ambient bed (hits/footsteps are one-shots via `playNoiseBurst`). Caller owns the returned nodes and must `.stop()`/`.disconnect()` them itself. */
export function startNoiseLoop(context: AudioContext, destination: AudioNode, filterType: BiquadFilterType, filterFreq: number, gain: number): { source: AudioBufferSourceNode; filter: BiquadFilterNode; gainNode: GainNode } {
  const buffer = getNoiseBuffer(context, 4); // long enough that the loop seam is inaudible under a drone
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  const filter = context.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.value = filterFreq;

  const gainNode = context.createGain();
  gainNode.gain.value = gain;

  source.connect(filter).connect(gainNode).connect(destination);
  source.start();
  return { source, filter, gainNode };
}
